import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class UserSession extends ModelObject<UserSession> {

    public tenantGroupId: Guid = Guid.createEmpty();
    public id: Guid = Guid.createEmpty();
    public userId: Guid = Guid.createEmpty();
    /**
    * The session token expected on every request for this session
    * Not Nullable
    * Not Empty
    * Max Length : 32
    * Preserves leading and trailing whitespace.
    */
    public token: string = "";
    /**
    * Whether the current session holds a license.
    */
    public isLicensed: boolean = false;
    /**
    * Whether the current session is for a support user.
    */
    public isSupportUser: boolean = false;
    /**
    * A string that identifies a browser instance. This is not guaranteed to be unique, and can be changed by the user at any time.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public browserInstanceId: string = "";
    /**
    * Whether the current session is for a system user.
    */
    public isSystemUser: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.token = jsonSerializedObject.token;
          this.isLicensed = jsonSerializedObject.isLicensed;
          this.isSupportUser = jsonSerializedObject.isSupportUser;
          this.browserInstanceId = jsonSerializedObject.browserInstanceId;
          this.isSystemUser = jsonSerializedObject.isSystemUser;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantGroupId = this.tenantGroupId.toString();
        toRet.id = this.id.toString();
        toRet.userId = this.userId.toString();
        toRet.token = this.token;
        toRet.isLicensed = this.isLicensed;
        toRet.isSupportUser = this.isSupportUser;
        toRet.browserInstanceId = this.browserInstanceId;
        toRet.isSystemUser = this.isSystemUser;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "UserSession.TenantGroupId cannot be empty"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "UserSession.Id cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "UserSession.UserId cannot be empty"},
            {field: "token", rule: (v: any) => v == null || v.length !== 0 || "Token cannot be empty"},
            {field: "token", rule: (v: any) => v == null || v.length <= 32 || "Token has too many characters (32)"},
    ];
}


export class LicensedUserSession extends ModelObject<LicensedUserSession> {

    public sessionId: Guid = Guid.createEmpty();
    /**
    * Last Access timestamp
    */
    public lastAccess: Instant = Instant.ofEpochMilli(0);
    public userId: Guid = Guid.createEmpty();
    public isCurrentSession: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sessionId = Guid.parseOrEmpty(jsonSerializedObject.sessionId);
          this.lastAccess = Instant.parse(jsonSerializedObject.lastAccess);
          this.userId = Guid.parseOrEmpty(jsonSerializedObject.userId);
          this.isCurrentSession = jsonSerializedObject.isCurrentSession;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sessionId = this.sessionId.toString();
        toRet.lastAccess = this.lastAccess.toString();
        toRet.userId = this.userId.toString();
        toRet.isCurrentSession = this.isCurrentSession;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sessionId", rule: (v: any) => !v.isEmpty() || "LicensedUserSession.SessionId cannot be empty"},
            {field: "userId", rule: (v: any) => !v.isEmpty() || "LicensedUserSession.UserId cannot be empty"},
    ];
}


export class UserSessionService extends ServiceBase {

  public async tryGetLicenseForCurrentSession(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    };
    const extractor = (response:any) => response.hasLicense;;
    return this.makeJsonRequest("userSession/v1/tryGetLicenseForCurrentSession", req, false, extractor, opts);
  };

  public async getAllLicensedUserSessions(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(LicensedUserSession)[]> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = (response:any) => response.licensedUserSessions.map((v: any) => new LicensedUserSession(v));;
    return this.makeJsonRequest("userSession/v1/getAllLicensedUserSessions", req, false, extractor, opts);
  };

  public async kickLicensedUserSession(tenantGroupId: Guid, sessionId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    sessionId:sessionId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("userSession/v1/kickLicensedUserSession", req, true, extractor, opts);
  };

  public async createUserSessionForAnotherTenant(targetTenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<UserSession> {
    const req = {
    tenantId:api.tenantId.toString(),
    targetTenantId:targetTenantId.toString(),
    };
    const extractor = (response:any) => new UserSession(response.userSession);;
    return this.makeJsonRequest("userSession/v1/createUserSessionForAnotherTenant", req, false, extractor, opts);
  };

  public async swapUserSessionForAnotherTenantGroup(targetTenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<UserSession | null> {
    const req = {
    targetTenantGroupId:targetTenantGroupId.toString(),
    };
    const extractor = (response:any) => response.userSession == null ? null : new UserSession(response.userSession);;
    return this.makeJsonRequest("userSession/v1/swapUserSessionForAnotherTenantGroup", req, false, extractor, opts);
  };
}

export const userSessionService = new UserSessionService();